import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { m } from 'framer-motion';
import { Iconify } from 'src/components';
import { overallConversion, pctOf } from '../byTreatmentMetrics';

const DEFAULT_COLORS = [
    'linear-gradient(90deg, #10b981, #34d399)',
    'linear-gradient(90deg, #3b82f6, #60a5fa)',
    'linear-gradient(90deg, #6366f1, #818cf8)',
    'linear-gradient(90deg, #8b5cf6, #a78bfa)',
    'linear-gradient(90deg, #f59e0b, #fbbf24)',
    'linear-gradient(90deg, #ef4444, #f87171)',
    'linear-gradient(90deg, #64748b, #94a3b8)',
];

function FunnelRow({ item, index, width, onStageClick, sideLabel, sideWide }) {
    return (
        <Box
            component={m.div}
            variants={{
                initial: { opacity: 0, scaleX: 0, originX: 0 },
                animate: {
                    opacity: 1,
                    scaleX: 1,
                    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
                },
            }}
        >
            <Box
                data-testid={`funnel-stage-${item.stage}`}
                onClick={() => onStageClick && onStageClick(item.stage)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: onStageClick ? 'pointer' : 'default',
                    transition: 'transform 0.2s ease',
                    '&:hover': onStageClick ? {
                        transform: 'translateX(4px)',
                    } : {},
                }}
            >
                <Box
                    sx={{
                        width: `${width}%`,
                        height: '48px',
                        borderRadius: '8px',
                        background: item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        color: '#fff',
                        fontWeight: 600,
                        transition: 'width 0.5s ease',
                        minWidth: '150px',
                    }}
                >
                    <Tooltip title={item.caption || ''} placement="top" arrow disableHoverListener={!item.caption}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            {item.stage}
                            {item.caption && (
                                <Typography
                                    component="span"
                                    data-testid={`funnel-caption-${item.stage}`}
                                    sx={{ display: 'block', fontSize: '11px', fontWeight: 400, opacity: 0.9, lineHeight: 1.1 }}
                                >
                                    {item.caption}
                                </Typography>
                            )}
                        </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                        {item.value?.toLocaleString() || 0}
                    </Typography>
                </Box>

                {sideLabel && (
                    <Box
                        data-testid={`funnel-ratio-${item.stage}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: sideWide ? '130px' : '80px',
                            color: '#64748b',
                            fontSize: '13px',
                            fontWeight: 500,
                            ml: 2,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {!sideWide && <Iconify icon="eva:arrow-forward-fill" width={16} height={16} sx={{ mr: 0.5 }} />}
                        {sideLabel}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

FunnelRow.propTypes = {
    item: PropTypes.shape({ stage: PropTypes.string, value: PropTypes.number, color: PropTypes.string, caption: PropTypes.string }).isRequired,
    index: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    onStageClick: PropTypes.func,
    sideLabel: PropTypes.string,
    sideWide: PropTypes.bool,
};
FunnelRow.defaultProps = { onStageClick: null, sideLabel: null, sideWide: false };

export default function ConversionFunnel({ data = [], title, onStageClick }) {
    if (!data || data.length === 0) {
        return null;
    }
    // `branch: true` stages (e.g. Failed to Attend, Archived) are side outcomes, not
    // steps on the main path: they never chain into the next bar's ratio, are drawn
    // separately and report "% of enquiries" (their share of the first main stage).
    const mainData = data.filter((d) => !d.branch);
    const branchData = data.filter((d) => d.branch);
    const enquiries = mainData[0]?.value || 0;
    const maxValue = Math.max(...data.map((d) => d.value), 1);

    const calculateConversion = (from, to) => {
        if (from === 0) return 0;
        return ((to / from) * 100).toFixed(1);
    };

    const calculateWidth = (value) => {
        const percentage = (value / maxValue) * 100;
        return Math.max(percentage, 20); // Minimum 20% width for visibility
    };

    return (
        <Box
            component={m.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                mb: 3,
            }}
        >
            {/* Header: title */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Tooltip title="Shows how leads progress through your pipeline from enquiry to treatment." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
            </Box>

            <Box
                component={m.div}
                initial="initial"
                animate="animate"
                variants={{
                    initial: {},
                    animate: { transition: { staggerChildren: 0.15, delayChildren: 0.5 } },
                }}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                {mainData.map((item, index) => (
                    <FunnelRow
                        key={item.stage}
                        item={item}
                        index={index}
                        width={calculateWidth(item.value)}
                        onStageClick={onStageClick}
                        sideLabel={index < mainData.length - 1
                            ? `${calculateConversion(item.value, mainData[index + 1]?.value)}%`
                            : null}
                    />
                ))}
                {branchData.length > 0 && (
                    <Box
                        data-testid="funnel-side-branches"
                        sx={{ mt: 1, pt: 2, borderTop: '1px dashed #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Side outcomes
                        </Typography>
                        {branchData.map((item, index) => (
                            <FunnelRow
                                key={item.stage}
                                item={item}
                                index={mainData.length + index}
                                width={calculateWidth(item.value)}
                                onStageClick={onStageClick}
                                sideLabel={`${pctOf(item.value, enquiries).toFixed(1)}% of enquiries`}
                                sideWide
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Summary stats */}
            {mainData.length >= 2 && (
                <Box
                    component={m.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 1.2 }}
                    sx={{
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        gap: 4,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>
                            Overall Conversion
                        </Typography>
                        <Typography data-testid="funnel-overall-conversion" sx={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>
                            {overallConversion(enquiries, mainData[mainData.length - 1]?.value).pct.toFixed(1)}%
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>
                            Drop-off
                        </Typography>
                        <Typography data-testid="funnel-drop-off" sx={{ fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>
                            {overallConversion(enquiries, mainData[mainData.length - 1]?.value).dropOff}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

ConversionFunnel.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            stage: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
            color: PropTypes.string,
            // Side outcome (not on the main path) — see ConversionFunnel.
            branch: PropTypes.bool,
        })
    ),
    title: PropTypes.string,
    onStageClick: PropTypes.func,
};

ConversionFunnel.defaultProps = {
    data: [],
    title: 'Conversion Funnel',
    onStageClick: null,
};
